// 환경 배치(순수 함수, three 없음): levels.ts 격자에서 키트 인스턴스·장식·고원 마스크를 시드 고정으로 뽑는다.
// 충돌·길찾기는 격자 그대로다. 여기서는 보이는 것만 정한다.
import { Cell, type MapDef, type MapGrid } from '../../shared/levels';
import { layoutEmber } from './emberLayout';
import { hash2, mulberry32, valueNoise } from './rng';

export type KitKind =
  | 'wall' | 'wall_broken' | 'pillar' | 'pillar_broken' | 'arch' | 'stairs' | 'statue' | 'banner' | 'brazier'
  | 'rubble' | 'rock' | 'tree' | 'crates' | 'mushrooms'
  /** 고원 바깥 장식용 저폴리 변형(나무 2.5k, 바위 1k 삼각형). 플레이 바닥 가까운 랜드마크는 원본을 쓴다. */
  | 'tree_lo' | 'rock_lo'
  | 'v_house' | 'v_lamppost' | 'v_gate' | 'v_shop' | 'v_fountain' | 'v_forge' | 'v_questboard' | 'v_trainer'
  /** 에셋 파운드리 마을 소품(카탈로그 village_*) */
  | 'village_well' | 'village_market_stall' | 'village_flower_planter' | 'village_banner_pole' | 'village_house_b';

/** 0 컷 없음, 1 카메라 쪽이면 낮춘다, 2 카메라 쪽이면 디더로 비친다 */
export type CutMode = 0 | 1 | 2;

/**
 * 키트 인스턴스 하나. 크기 지정 방식:
 * h>0, w=d=0 → 높이에 맞춘 균일 배율 / h,w,d>0 → 축별 배율 / h=0, w,d>0 → 바닥 면적에 맞춘 균일 배율
 */
export interface Placement {
  kind: KitKind;
  x: number;
  y: number;
  z: number;
  /** 정면(+Z)이 향할 방향(rad, 0 = +z) */
  rot: number;
  h: number;
  w: number;
  d: number;
  /** 인스턴스 색 곱(0xRRGGBB) */
  tint: number;
  cut: CutMode;
  /** 이 배치가 대신하는 랜드마크 인덱스(키트를 못 읽으면 그 랜드마크는 그레이박스로 남는다) */
  landmark: number;
  /** 랜드마크 충돌 사각형을 함께 채우는 부속물이면 그 랜드마크 인덱스 */
  part?: number;
  /** 키트를 못 읽었을 때 대신 쓸 배치 */
  alt?: Placement;
}

/** 점 효과(불꽃·발광·풀 카드·빛줄기): s = 크기 */
export interface Emitter {
  x: number;
  y: number;
  z: number;
  s: number;
  /** 0..1 개체별 변주 */
  v: number;
}

/** 고원(풀밭 띠) 점 격자: f>0 이면 땅. 점 (i,j)의 월드 좌표 = origin + (i, j) */
export interface PlateauField {
  originX: number;
  originZ: number;
  /** 점 개수 */
  nx: number;
  nz: number;
  f: Float32Array;
}

/** 바닥 셀별 벽 근접도 0..255(벽 밑 그늘·이끼) */
export interface FloorShade {
  originX: number;
  originZ: number;
  width: number;
  height: number;
  data: Uint8Array;
}

export interface EnvLayout {
  placements: Placement[];
  flames: Emitter[];
  glows: Emitter[];
  tufts: Emitter[];
  flowers: Emitter[];
  shafts: Emitter[];
  /** 청록 물웅덩이(콘셉트 r1-dungeon의 물빛): s = 반경 */
  pools: Emitter[];
  /** 등불 머리의 따뜻한 발광(마을 가로등) */
  lamps: Emitter[];
  /** 벽에서 늘어진 담쟁이(R1): y = 윗끝 높이, s = 길이, rot = 벽 안쪽 면이 보는 방향, w = 폭 */
  ivy: (Emitter & { rot: number; w: number })[];
  plateau: PlateauField;
  shade: FloorShade;
}

export interface LayoutOptions {
  seed: number;
  /** 장식 밀도 배율(low 0.5, mid 0.75, high 1) */
  density: number;
}

/** 마을 소품 바닥 발자국(m, 회전 전 W×D) — 배치 겹침 검사용(실제 크기는 로드 뒤 bbox) */
const FOOT: Partial<Record<KitKind, readonly [number, number]>> = {
  village_market_stall: [3.0, 1.75], village_well: [2.0, 2.0], village_flower_planter: [1.35, 1.35],
  village_banner_pole: [1.9, 0.8], v_lamppost: [1.5, 0.7], crates: [1.6, 1.1],
};

/** 회전을 반영한 발자국(W, D) */
export function footprintOf(kind: KitKind, rot: number): [number, number] {
  const f = FOOT[kind] ?? [1, 1];
  const q = Math.round(rot / (Math.PI / 2)) & 1;
  return q ? [f[1], f[0]] : [f[0], f[1]];
}

export interface ZoneRect {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
}

/** 마을에서 소품을 두지 않는 구역: 중앙 통로(스폰 → 분수 → 던전 문), 분수 둘레, 시설 입구 앞 */
export function villageClearZones(map: MapDef): ZoneRect[] {
  const z: ZoneRect[] = [{ x0: -3.4, z0: -30, x1: 3.4, z1: 30 }, { x0: -4.8, z0: -4.8, x1: 4.8, z1: 4.8 }];
  for (const l of map.landmarks) {
    const r = l.rect, cx = (r.x0 + r.x1) / 2;
    if (l.kind === 'shop' || l.kind === 'forge' || l.kind === 'trainer') {
      // 정면(광장 쪽) 3m. 모서리 1.2m는 입구 양옆 장식 자리로 남긴다
      if (cx < 0) z.push({ x0: r.x1, z0: r.z0 + 1.2, x1: r.x1 + 3, z1: r.z1 - 1.2 });
      else z.push({ x0: r.x0 - 3, z0: r.z0 + 1.2, x1: r.x0, z1: r.z1 - 1.2 });
    } else if (l.kind === 'board') z.push({ x0: r.x0, z0: r.z1, x1: r.x1, z1: r.z1 + 2.5 });
  }
  return z;
}

/** 키트 원본 치수(m, 로드 뒤 bbox로 다시 잰다) — 벽 두께 오프셋 계산용 */
export const KIT_DEPTH = { wall: 0.74, wall_broken: 1.0 } as const;

const PI = Math.PI;
const MARGIN = 18;

export function layoutMap(map: MapDef, grid: MapGrid, opt: LayoutOptions): EnvLayout {
  if (map.id === 'r2') return layoutEmber(map, grid, opt);
  const village = map.id === 'village';
  const rnd = mulberry32(opt.seed);
  const W = grid.width, H = grid.height, ox = grid.originX, oz = grid.originZ;
  const cell = (gx: number, gz: number): number => (gx < 0 || gz < 0 || gx >= W || gz >= H ? Cell.Void : grid.cells[gz * W + gx]);
  const gateCell = (gx: number, gz: number): number => (gx < 0 || gz < 0 || gx >= W || gz >= H ? 0 : grid.gateCells[gz * W + gx]);
  const cellW = (x: number, z: number): number => cell(Math.floor(x - ox), Math.floor(z - oz));
  const placements: Placement[] = [];
  const flames: Emitter[] = [];
  const glows: Emitter[] = [];
  const tufts: Emitter[] = [];
  const flowers: Emitter[] = [];
  const shafts: Emitter[] = [];
  const pools: Emitter[] = [];
  // 물웅덩이는 따로 난수를 써서 다른 배치(벽·장식)의 시드 흐름을 바꾸지 않는다
  const rndPool = mulberry32(opt.seed + 991);
  const P = (kind: KitKind, x: number, z: number, rot: number, h: number, w = 0, d = 0, cut: CutMode = 1, tint = 0xffffff, landmark = -1): Placement => {
    const p: Placement = { kind, x, y: 0, z, rot, h, w, d, tint, cut, landmark };
    placements.push(p);
    return p;
  };

  // 장식 금지 구역: 문 앞뒤 2.5m(통행로), 단단한 셀(랜드마크) 주변
  const nearGate = (x: number, z: number, pad: number): boolean => {
    for (const g of map.gates) if (x > g.rect.x0 - pad && x < g.rect.x1 + pad && z > g.rect.z0 - pad && z < g.rect.z1 + pad) return true;
    for (const e of map.exits) if (x > e.rect.x0 - pad && x < e.rect.x1 + pad && z > e.rect.z0 - pad && z < e.rect.z1 + pad) return true;
    return false;
  };
  /** 반경 r 안이 모두 바닥이고 문·랜드마크와 떨어져 있으면 장식 가능 */
  const floorFree = (x: number, z: number, r: number): boolean => {
    for (let dz = -r; dz <= r + 1e-6; dz += r) for (let dx = -r; dx <= r + 1e-6; dx += r) if (cellW(x + dx, z + dz) !== Cell.Floor) return false;
    return !nearGate(x, z, 2.5);
  };

  // ── 벽: 바닥(또는 랜드마크)과 빈칸의 경계선을 직선 구간으로 모아 2m 마디로 나눈다 ──
  const edges = new Map<string, number[]>();
  const pushEdge = (dir: number, line: number, along: number): void => {
    const key = dir + ':' + line;
    let a = edges.get(key);
    if (!a) edges.set(key, (a = []));
    a.push(along);
  };
  for (let gz = 0; gz < H; gz++) {
    for (let gx = 0; gx < W; gx++) {
      if (cell(gx, gz) === Cell.Void) continue;
      if (cell(gx, gz - 1) === Cell.Void) pushEdge(0, gz, gx); // 북쪽이 빈칸: 벽은 북쪽, 정면은 +z
      if (cell(gx, gz + 1) === Cell.Void) pushEdge(1, gz + 1, gx); // 남쪽(카메라 쪽)
      if (cell(gx - 1, gz) === Cell.Void) pushEdge(2, gx, gz); // 서쪽
      if (cell(gx + 1, gz) === Cell.Void) pushEdge(3, gx + 1, gz); // 동쪽
    }
  }
  const NORMAL = [[0, 1], [0, -1], [1, 0], [-1, 0]] as const; // 바닥 쪽(안쪽) 법선
  const ROT = [0, PI, PI / 2, -PI / 2];
  const corners = new Map<string, [number, number]>();
  interface Seg { x: number; z: number; nx: number; nz: number; len: number; dir: number; broken: boolean }
  const segs: Seg[] = [];
  const keys = [...edges.keys()].sort();
  for (const key of keys) {
    const [dirS, lineS] = key.split(':');
    const dir = Number(dirS), line = Number(lineS);
    const al = edges.get(key)!.sort((a, b) => a - b);
    let s = 0;
    while (s < al.length) {
      let e = s;
      while (e + 1 < al.length && al[e + 1] === al[e] + 1) e++;
      const a = al[s], b = al[e] + 1, L = b - a;
      for (const pt of [a, b]) {
        const gx = dir < 2 ? pt : line, gz = dir < 2 ? line : pt;
        corners.set(gx + ',' + gz, [gx, gz]);
      }
      const n = Math.max(1, Math.round(L / 2));
      const seg = L / n;
      const [nx, nz] = NORMAL[dir];
      for (let i = 0; i < n; i++) {
        const along = a + seg * (i + 0.5);
        const bx = ox + (dir < 2 ? along : line), bz = oz + (dir < 2 ? line : along); // 경계선 위 점
        const r = rnd();
        // 카메라 쪽(남쪽) 벽은 무너진 벽을 더 많이 섞어 가림을 줄인다
        const broken = !village && r < (dir === 1 ? 0.42 : 0.14);
        const off = broken ? 0.5 : KIT_DEPTH.wall / 2;
        const t = rnd();
        const tint = village ? 0xfff2df : t < 0.3 ? 0xcfe3bd : t < 0.5 ? 0xdcd5c9 : t < 0.6 ? 0xfff6e6 : 0xffffff;
        const x = bx - nx * off, z = bz - nz * off;
        if (broken) P('wall_broken', x, z, ROT[dir], 1.5 + rnd() * 0.5, seg * 1.14, 1.0, 1, tint);
        else P('wall', x, z, ROT[dir], village ? 1.5 : 3.0 + rnd() * 0.25, seg * 1.07, KIT_DEPTH.wall, 1, tint);
        segs.push({ x: bx, z: bz, nx, nz, len: seg, dir, broken });
      }
      s = e + 1;
    }
  }

  // 모서리 기둥: 경계 꺾임점마다 빈칸 쪽으로 밀어 세운다(문 옆은 아치 기둥이 대신한다)
  for (const [gx, gz] of corners.values()) {
    let voids = 0, ax = 0, az = 0, gate = false;
    for (let k = 0; k < 4; k++) {
      const cx = gx - 1 + (k & 1), cz = gz - 1 + (k >> 1);
      if (gateCell(cx, cz)) gate = true;
      if (cell(cx, cz) === Cell.Void) { voids++; ax += cx + 0.5 - gx; az += cz + 0.5 - gz; }
    }
    if (gate || voids === 0 || voids === 4) continue;
    const len = Math.hypot(ax, az);
    if (len < 1e-3) continue;
    const x = ox + gx + (ax / len) * 0.45, z = oz + gz + (az / len) * 0.45;
    if (village) P('pillar', x, z, 0, 2.3, 0.95, 0.95, 1, 0xfff2df);
    else if (rnd() < 0.22) P('pillar_broken', x, z, rnd() * PI * 2, 2.1 + rnd() * 0.4, 1.6, 1.6, 1);
    else P('pillar', x, z, 0, 4.0 + rnd() * 0.6, 1.35, 1.35, 1);
  }

  // ── 문: 아치(기둥이 문 양옆 벽 모서리에 서도록 늘린다). 문짝(열림/닫힘)은 map.ts가 그린다 ──
  for (const g of map.gates) {
    const gw = g.rect.x1 - g.rect.x0, gd = g.rect.z1 - g.rect.z0;
    const horiz = gw >= gd;
    const span = (horiz ? gw : gd) + 0.7;
    const cx = (g.rect.x0 + g.rect.x1) / 2, cz = (g.rect.z0 + g.rect.z1) / 2;
    // 원본 아치: 폭 4.43m, 기둥 중심 간격 약 3.36m
    P('arch', cx, cz, horiz ? 0 : PI / 2, 5.2 + span * 0.08, (4.43 * span) / 3.36, 1.7, 1);
    // 방 쪽 양옆 화로
    const side = horiz ? (cellW(g.rect.x0 - 2.5, g.rect.z0 - 1.5) === Cell.Floor ? -1 : 1) : 0;
    if (horiz && side !== 0) {
      const zr = (side < 0 ? g.rect.z0 : g.rect.z1) + side * 1.5;
      for (const x of [g.rect.x0 - 1.4, g.rect.x1 + 1.4]) {
        if (cellW(x, zr) !== Cell.Floor) continue;
        placeBrazier(x, zr);
      }
    }
  }

  function placeBrazier(x: number, z: number): void {
    const h = 1.5;
    P('brazier', x, z, rnd() * PI * 2, h, 0, 0, 1);
    flames.push({ x, y: h * 0.96, z, s: 0.9, v: rnd() });
  }
  function placeMushrooms(x: number, z: number, h: number): void {
    P('mushrooms', x, z, rnd() * PI * 2, h, 0, 0, 1);
    glows.push({ x, y: h * 0.55, z, s: h * 2.3, v: rnd() });
  }

  // ── 랜드마크 ──
  map.landmarks.forEach((l, i) => {
    const w = l.rect.x1 - l.rect.x0, d = l.rect.z1 - l.rect.z0;
    const cx = (l.rect.x0 + l.rect.x1) / 2, cz = (l.rect.z0 + l.rect.z1) / 2;
    switch (l.kind) {
      case 'statue':
        P('statue', cx, cz, 0, 4.8, 0, 0, 2, 0xffffff, i);
        // 여신상 하나로는 6×2m 충돌 사각형이 비어 보여 양옆을 잔해로 채운다
        P('rubble', cx - 2.2, cz + 0.1, 0.6, 0.9, 1.7, 1.6, 1).part = i;
        P('rubble', cx + 2.2, cz - 0.1, 2.4, 0.8, 1.6, 1.5, 1).part = i;
        break;
      case 'pillar':
        if (l.height < 5 && rnd() < 0.25) {
          P('pillar_broken', cx, cz, rnd() * PI * 2, 2.4, 1.9, 1.9, 1, 0xffffff, i);
          P('rubble', cx + (rnd() - 0.5) * 2, cz + 1.4, rnd() * PI, 0.6, 0, 0, 1);
        } else {
          const s = l.height / 4.5;
          P('pillar', cx, cz, (rnd() * 4 | 0) * (PI / 2), l.height, 1.55 * s, 1.55 * s, 2, 0xffffff, i);
        }
        break;
      case 'rubble':
        P('rubble', cx, cz, rnd() * PI * 2, 1.25, w * 0.95, d * 0.9, 1, 0xffffff, i);
        break;
      case 'tree':
        P('tree', cx, cz, rnd() * PI * 2, l.height * 1.15, 0, 0, 2, 0xffffff, i);
        break;
      case 'altar':
        // C2 지역6 처형대: 낮은 단(충돌 사각형) 위에 악마 파수상(지역판 statue), 양옆 보랏빛 화로. 지역2 스테이지 제단은 세트피스가 그린다
        if (map.id !== 'r6') break;
        P('statue', cx, cz, 0, 3.6, 0, 0, 2, 0xffffff, i);
        for (const sx of [-1, 1]) {
          P('brazier', cx + sx * (w / 2 + 0.7), cz + d / 2 - 0.3, 0, 1.5, 0, 0, 1).part = i;
          flames.push({ x: cx + sx * (w / 2 + 0.7), y: 1.45, z: cz + d / 2 - 0.3, s: 1.0, v: rnd() });
        }
        break;
      case 'fountain':
        // 광장 중심 표식(충돌 6m). 1.22배는 테두리가 화면에서 너무 커 보였다(판정 3회차) → 충돌보다 5%만 크게
        P('v_fountain', cx, cz, 0, 0, w * 1.05, d * 1.05, 0, 0xffffff, i);
        break;
      // 마을 건물은 정면(+Z)이 광장 중심을 보게 돌린다. 잘라내면 평면도처럼 보여 카메라 쪽이면 디더로 비친다
      case 'shop':
        P('v_shop', cx, cz, cx < 0 ? PI / 2 : -PI / 2, 0, w, d, 2, 0xffffff, i);
        break;
      case 'forge':
        P('v_forge', cx, cz, cx < 0 ? PI / 2 : -PI / 2, 0, w, d, 2, 0xffffff, i);
        break;
      case 'trainer':
        P('v_trainer', cx, cz, cx < 0 ? PI / 2 : -PI / 2, 0, w, d, 2, 0xffffff, i);
        break;
      case 'board':
        P('v_questboard', cx, cz, 0, 3.2, 0, 0, 2, 0xffffff, i);
        break;
      case 'stall': {
        // 장터 골목 노점: 정면이 중앙 통로를 본다. 통로 쪽 1.75m에 노점, 바깥쪽에 깃대, 한쪽 끝에 나무상자
        const side = cx < 0 ? -1 : 1;
        const rot = side < 0 ? PI / 2 : -PI / 2;
        const inner = side < 0 ? l.rect.x1 - 0.95 : l.rect.x0 + 0.95;
        const outer = side < 0 ? l.rect.x0 + 0.45 : l.rect.x1 - 0.45;
        // 노점은 화면에서 읽히게 크게(높이 3m): 차양 무늬·상품이 1280×720에서 보인다
        P('village_market_stall', inner + side * -0.15, cz - 0.2, rot, 3.0, 0, 0, 1, 0xffffff, i);
        P('village_banner_pole', outer, cz - 0.9, rot, 4.0, 0, 0, 1).part = i;
        P('crates', outer + side * -0.2, l.rect.z1 - 0.55, rnd() * 0.4 - 0.2, 0.85, 0, 0, 1, 0xf2e2c8).part = i;
        // 통로 쪽 모서리 가로등: 낮은 해에 긴 그림자가 돌길을 가로지른다(콘셉트의 줄무늬 그림자)
        P('v_lamppost', side < 0 ? l.rect.x1 - 0.3 : l.rect.x0 + 0.3, l.rect.z1 - 0.3, 0, 3.6, 0, 0, 1).part = i;
        // 노점 앞(통로 쪽) 꽃 띠: 콘셉트처럼 노점 둘레를 꽃이 덮는다
        for (let k = 0; k < 9; k++) flowers.push({ x: inner + side * -1.05 + (rnd() - 0.5) * 0.45, y: 0, z: l.rect.z0 + 0.2 + rnd() * (d - 0.4), s: 0.45 + rnd() * 0.3, v: rnd() });
        break;
      }
      case 'decor': {
        // 카메라 앞 전경 장식: 가로등(랜드마크 대표) + 깃대 + 꽃 상자 두 개 + 꽃 무더기, 통로 반대쪽으로 비켜 선다
        const side = cx < 0 ? -1 : 1;
        P('v_lamppost', cx, cz - 0.6, 0, 3.8, 0, 0, 1, 0xffffff, i);
        P('village_banner_pole', cx + side * 0.9, cz + 0.4, side < 0 ? PI / 2 : -PI / 2, 4.2, 0, 0, 1).part = i;
        // 목표 화면 왼쪽 아래처럼 꽃 상자가 화면 모서리를 크게 채운다(0.95m 상자는 전경이 아니라 작은 소품으로 읽혔다)
        P('village_flower_planter', cx + side * 0.2, cz + 0.9, 0, 1.25, 0, 0, 1).part = i;
        P('village_flower_planter', cx + side * 1.5, cz - 0.4, 0.3, 1.1, 0, 0, 1).part = i;
        P('village_flower_planter', cx + side * 1.2, cz + 2.0, -0.2, 1.15, 0, 0, 1).part = i;
        P('crates', cx + side * 2.4, cz + 1.0, 0.35, 0.9, 0, 0, 1, 0xf2e2c8).part = i;
        for (let k = 0; k < 16; k++) flowers.push({ x: cx + side * (0.1 + rnd() * 2.2), y: 0, z: cz - 1 + rnd() * 3.4, s: 0.55 + rnd() * 0.4, v: rnd() });
        break;
      }
      case 'gate': {
        // v_gate(8.7×7×5.4m)는 앞 계단이 길 쪽(+z)으로 나온다: 아치가 사각형 줄에 오도록 앞으로 민다
        const p = P('v_gate', cx, cz + 1.6, 0, 7.4, 0, 0, 0, 0xffffff, i);
        if (l.label === '잿불 협곡') p.tint = 0xff7040;
        p.alt = { kind: 'arch', x: cx, y: 0, z: cz, rot: 0, h: 7.2, w: (4.43 * (w + 0.7)) / 3.36, d: 2.0, tint: 0xffffff, cut: 0, landmark: i };
        if (l.label === '잿불 협곡') {
          p.alt.tint = p.tint;
          for (const side of [-1, 1]) {
            const bx = cx + side * 3.1;
            P('brazier', bx, cz + 2.2, 0, 1.7, 0, 0, 0, 0x75483b).part = i;
            flames.push({ x: bx, y: 1.65, z: cz + 2.2, s: 1.1, v: 0.5 });
          }
        }
        break;
      }
    }
  });

  // ── 방 안: 모서리 소품, 벽 밑 이끼·버섯·풀 ──
  if (!village) {
    for (const room of map.rooms) {
      const a = room.area;
      const pts: [number, number][] = [[a.x0 + 1.7, a.z0 + 1.7], [a.x1 - 1.7, a.z0 + 1.7], [a.x0 + 1.7, a.z1 - 1.7], [a.x1 - 1.7, a.z1 - 1.7]];
      for (const [x, z] of pts) {
        if (!floorFree(x, z, 1)) continue;
        const r = rnd();
        if (r < 0.34) placeBrazier(x, z);
        else if (r < 0.66) {
          P('crates', x, z, rnd() * PI * 2, 1.3, 0, 0, 1);
          if (rnd() < 0.5) placeMushrooms(x + 1.3, z + (z < (a.z0 + a.z1) / 2 ? 0.6 : -0.6), 0.75);
        } else if (r < 0.86) {
          placeMushrooms(x, z, 1.0);
          placeMushrooms(x + 1.1, z + 0.5, 0.7);
        } else P('tree', x, z, rnd() * PI * 2, 5.6, 0, 0, 2);
      }
      // 빛줄기 2개(콘셉트처럼 굵게)
      for (let k = 0; k < 2; k++) {
        shafts.push({ x: a.x0 + 4 + rnd() * (a.x1 - a.x0 - 8), y: 0, z: a.z0 + 4 + rnd() * (a.z1 - a.z0 - 8), s: 4.2 + rnd() * 2.6, v: rnd() });
      }
      // 방 가운데 근처 빛줄기 1개(전투 카메라가 방 가운데를 볼 때 화면 안에 들어오게, 콘셉트 r1-dungeon)
      shafts.push({ x: (a.x0 + a.x1) / 2 - 4 - rndPool() * 3, y: 0, z: (a.z0 + a.z1) / 2 - 3 - rndPool() * 3, s: 5.2, v: rndPool() });
      // 청록 물웅덩이: 양쪽 벽 가까이, 방 가운데 높이(걷는 바닥 가운데는 비운다) + 곁에 버섯
      for (const side of [-1, 1]) {
        if (rndPool() > 0.75) continue;
        const r0 = 1.5 + rndPool() * 1.1;
        const x = side < 0 ? a.x0 + r0 + 0.9 : a.x1 - r0 - 0.9;
        const z = a.z0 + (a.z1 - a.z0) * (0.3 + rndPool() * 0.4);
        if (!floorFree(x, z, r0) || !floorFree(x, z, r0 * 0.5)) continue;
        pools.push({ x, y: 0, z, s: r0, v: rndPool() });
        const mx = x - side * (r0 * 0.5), mz = z + (rndPool() < 0.5 ? -1 : 1) * (r0 + 0.3);
        if (floorFree(mx, mz, 0.5)) {
          P('mushrooms', mx, mz, rndPool() * PI * 2, 0.8, 0, 0, 1);
          glows.push({ x: mx, y: 0.45, z: mz, s: 1.8, v: rndPool() });
        }
      }
      // 화면 안 물웅덩이 하나: 방 가운데에서 한쪽으로 8m 남짓, 방 가운데보다 4~6m 북쪽(전투 카메라 화면 위쪽 가장자리, 몹에 덜 가린다)
      if (room.kind === 'battle') {
        const side = rndPool() < 0.5 ? -1 : 1;
        const r0 = 1.7 + rndPool() * 0.5;
        const x = (a.x0 + a.x1) / 2 + side * (6.4 + rndPool()), z = (a.z0 + a.z1) / 2 - 4 - rndPool() * 2;
        if (floorFree(x, z, r0)) {
          pools.push({ x, y: 0, z, s: r0, v: rndPool() });
          const mx = x + side * (r0 + 0.4), mz = z - r0 * 0.4;
          if (floorFree(mx, mz, 0.5)) {
            P('mushrooms', mx, mz, rndPool() * PI * 2, 0.75, 0, 0, 1);
            glows.push({ x: mx, y: 0.45, z: mz, s: 1.6, v: rndPool() });
          }
        }
      }
      // 보스방 둘레: 벽 밑 2~4m 띠에 잔해·버섯·꽃 무더기, 좌우 물웅덩이 한 쌍(콘셉트 c-boss-fight의 무성한 가장자리)
      if (room.kind === 'boss') {
        for (const side of [-1, 1]) {
          const r0 = 2.2;
          const x = side < 0 ? a.x0 + 4.2 : a.x1 - 4.2, z = (a.z0 + a.z1) / 2 + 2;
          if (floorFree(x, z, r0)) {
            pools.push({ x, y: 0, z, s: r0, v: rndPool() });
            // 웅덩이 둑: 가장자리에 낮은 돌·이끼 버섯(평평한 얼룩이 아니라 패인 물로 읽히게)
            for (let k = 0; k < 8; k++) {
              const a2 = (k / 8) * PI * 2 + rndPool() * 0.6;
              const bx = x + Math.cos(a2) * r0 * 1.02, bz = z + Math.sin(a2) * r0 * 0.85;
              if (k === 2 || k === 6) placeMushrooms(bx, bz, 0.55);
              else P('rock_lo', bx, bz, rndPool() * PI * 2, 0.35 + rndPool() * 0.25, 0, 0, 1);
            }
          }
        }
        for (let k = 0; k < 34; k++) {
          const t = rndPool();
          const edge = rndPool() < 0.5 ? 0 : 1;
          const x = edge === 0 ? (rndPool() < 0.5 ? a.x0 + 1.5 + rndPool() * 2.5 : a.x1 - 1.5 - rndPool() * 2.5) : a.x0 + 2 + t * (a.x1 - a.x0 - 4);
          const z = edge === 0 ? a.z0 + 2 + t * (a.z1 - a.z0 - 4) : a.z0 + 1.5 + rndPool() * 2.5;
          if (!floorFree(x, z, 0.6)) continue;
          const r = rndPool();
          if (r < 0.3) P('rubble', x, z, rndPool() * PI * 2, 0.5 + rndPool() * 0.3, 0, 0, 1);
          else if (r < 0.5) placeMushrooms(x, z, 0.6 + rndPool() * 0.3);
          for (let q = 0; q < 4; q++) (rndPool() < 0.55 ? flowers : tufts).push({ x: x + (rndPool() - 0.5) * 2.2, y: 0, z: z + (rndPool() - 0.5) * 2.2, s: 0.45 + rndPool() * 0.35, v: rndPool() });
        }
      }
      // 바닥 틈의 풀 포기·들꽃(이끼 정원은 더 무성하게). 문 앞 통행로는 비운다
      const lush = room.name.includes('정원') ? 2 : 1;
      for (let z = a.z0 + 1.2; z < a.z1 - 1; z += 2.2) {
        for (let x = a.x0 + 1.2; x < a.x1 - 1; x += 2.2) {
          if (rnd() > 0.16 * lush * opt.density) continue;
          const tx = x + (rnd() - 0.5) * 2, tz = z + (rnd() - 0.5) * 2;
          if (cellW(tx, tz) !== Cell.Floor || nearGate(tx, tz, 1.5)) continue;
          (rnd() < 0.3 ? flowers : tufts).push({ x: tx, y: 0, z: tz, s: 0.4 + rnd() * 0.35, v: rnd() });
        }
      }
    }
    shafts.push({ x: 0.5, y: 0, z: -4, s: 2.6, v: 0.3 });
  }
  for (const s of segs) {
    // 벽 밑(바닥 쪽) 장식
    const px = s.x + s.nx * 0.8, pz = s.z + s.nz * 0.8;
    if (!village && floorFree(px, pz, 0.5)) {
      const r = rnd();
      if (r < 0.1) placeMushrooms(px, pz, 0.7 + rnd() * 0.3);
      else if (r < 0.17) P('rubble', px, pz, rnd() * PI * 2, 0.45, 0, 0, 1);
    }
    const nt = Math.round((village ? 2 : 3) * opt.density + rnd());
    for (let k = 0; k < nt; k++) {
      const u = (rnd() - 0.5) * s.len;
      const dd = 0.25 + rnd() * 0.45;
      const tx = s.x + s.nx * dd + (s.nz !== 0 ? u : 0), tz = s.z + s.nz * dd + (s.nx !== 0 ? u : 0);
      if (cellW(tx, tz) !== Cell.Floor || nearGate(tx, tz, 1.5)) continue;
      (village && rnd() < 0.55 ? flowers : tufts).push({ x: tx, y: 0, z: tz, s: 0.45 + rnd() * 0.4, v: rnd() });
    }
  }

  // 깃발: 북쪽 벽(정면이 카메라 쪽) 앞에 일정 간격
  for (const key of keys) {
    const [dirS, lineS] = key.split(':');
    const dir = Number(dirS), line = Number(lineS);
    // 마을은 광장 깃대(village_banner_pole)가 대신한다
    if (dir !== 0 || village) continue;
    const al = edges.get(key)!;
    let s = 0;
    while (s < al.length) {
      let e = s;
      while (e + 1 < al.length && al[e + 1] === al[e] + 1) e++;
      const a = al[s], b = al[e] + 1;
      const step = village ? 7 : 8.5;
      for (let t = a + 3.2 + rnd() * 1.5; t < b - 3; t += step) {
        const x = ox + t, z = oz + line + 0.4;
        if (nearGate(x, z, 2.2) || cellW(x, z) !== Cell.Floor) continue;
        P('banner', x, z, 0, village ? 3.2 : 3.6, 0, 0, 1);
      }
      s = e + 1;
    }
  }

  // ── 고원: 바닥까지 거리장 + 잡음 반경. 그 바깥은 낭떠러지와 원경 ──
  const exW = W + MARGIN * 2, exH = H + MARGIN * 2;
  const exOX = ox - MARGIN, exOZ = oz - MARGIN;
  const dist = new Float32Array(exW * exH).fill(1e6);
  for (let j = 0; j < exH; j++) for (let i = 0; i < exW; i++) if (cell(i - MARGIN, j - MARGIN) !== Cell.Void) dist[j * exW + i] = 0;
  // 두 번 훑는 챔퍼 거리(1, √2)
  const D2 = Math.SQRT2;
  for (let j = 0; j < exH; j++) for (let i = 0; i < exW; i++) {
    let v = dist[j * exW + i];
    if (i > 0) v = Math.min(v, dist[j * exW + i - 1] + 1);
    if (j > 0) {
      v = Math.min(v, dist[(j - 1) * exW + i] + 1);
      if (i > 0) v = Math.min(v, dist[(j - 1) * exW + i - 1] + D2);
      if (i < exW - 1) v = Math.min(v, dist[(j - 1) * exW + i + 1] + D2);
    }
    dist[j * exW + i] = v;
  }
  for (let j = exH - 1; j >= 0; j--) for (let i = exW - 1; i >= 0; i--) {
    let v = dist[j * exW + i];
    if (i < exW - 1) v = Math.min(v, dist[j * exW + i + 1] + 1);
    if (j < exH - 1) {
      v = Math.min(v, dist[(j + 1) * exW + i] + 1);
      if (i < exW - 1) v = Math.min(v, dist[(j + 1) * exW + i + 1] + D2);
      if (i > 0) v = Math.min(v, dist[(j + 1) * exW + i - 1] + D2);
    }
    dist[j * exW + i] = v;
  }
  const distAt = (x: number, z: number): number => {
    const fx = x - exOX - 0.5, fz = z - exOZ - 0.5;
    const i = Math.max(0, Math.min(exW - 2, Math.floor(fx))), j = Math.max(0, Math.min(exH - 2, Math.floor(fz)));
    const u = Math.min(1, Math.max(0, fx - i)), v = Math.min(1, Math.max(0, fz - j));
    const a = dist[j * exW + i], b = dist[j * exW + i + 1], c = dist[(j + 1) * exW + i], d = dist[(j + 1) * exW + i + 1];
    return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
  };
  const rimBase = village ? 15 : 7.5, rimAmp = village ? 2.5 : 3.2;
  const rimAt = (x: number, z: number): number => rimBase + rimAmp * (valueNoise(x / 9, z / 9, opt.seed + 11) * 2 - 1) + 1.2 * (valueNoise(x / 3.1, z / 3.1, opt.seed + 23) * 2 - 1);
  const nx = exW + 1, nz = exH + 1;
  const f = new Float32Array(nx * nz);
  for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
    const x = exOX + i, z = exOZ + j;
    f[j * nx + i] = rimAt(x, z) - distAt(x, z);
  }
  const plateau: PlateauField = { originX: exOX, originZ: exOZ, nx, nz, f };

  // 고원 장식: 흔들린 격자 표본(바닥·벽 줄은 피한다)
  const scatter = (step: number, seed: number, fn: (x: number, z: number, d: number, rim: number) => void): void => {
    for (let z = exOZ + step / 2; z < exOZ + exH; z += step) {
      for (let x = exOX + step / 2; x < exOX + exW; x += step) {
        const jx = x + (hash2(Math.round(x * 7), Math.round(z * 7), seed) - 0.5) * step * 0.9;
        const jz = z + (hash2(Math.round(x * 7), Math.round(z * 7), seed + 1) - 0.5) * step * 0.9;
        const d = distAt(jx, jz);
        if (d < 0.8) continue;
        fn(jx, jz, d, rimAt(jx, jz));
      }
    }
  };
  const dens = opt.density;
  scatter(4.6, 101, (x, z, d, rim) => {
    if (d < 2.8 || d > rim - 1.4 || rnd() > 0.55 * (0.4 + 0.6 * dens)) return;
    P('tree_lo', x, z, rnd() * PI * 2, 5.2 + rnd() * 2.4, 0, 0, 2, rnd() < 0.3 ? 0xe8f0d0 : 0xffffff);
  });
  scatter(3.4, 202, (x, z, d, rim) => {
    if (d < 1.6 || d > rim - 0.3 || rnd() > 0.28 * dens) return;
    P('rock_lo', x, z, rnd() * PI * 2, 0.7 + rnd() * 1.1, 0, 0, 1);
  });
  if (!village) {
    scatter(7.5, 303, (x, z, d, rim) => {
      if (d < 3 || d > rim - 1 || rnd() > 0.3) return;
      const r = rnd();
      if (r < 0.4) P('wall_broken', x, z, rnd() * PI * 2, 1.4 + rnd() * 0.5, 0, 0, 1);
      else if (r < 0.7) P('pillar_broken', x, z, rnd() * PI * 2, 1.8 + rnd() * 0.6, 0, 0, 1);
      else P('rubble', x, z, rnd() * PI * 2, 0.8, 0, 0, 1);
    });
    scatter(2.6, 404, (x, z, d) => {
      if (d < 1.2 || d > 4.5 || rnd() > 0.09 * dens) return;
      placeMushrooms(x, z, 0.6 + rnd() * 0.5);
    });
  } else {
    // 마을 바깥: 두 종류 집을 섞어 촘촘하게(벽 너머로 지붕이 이어져 보이게), 정면은 광장 쪽
    scatter(7.2, 505, (x, z, d, rim) => {
      if (d < 4.2 || d > rim - 3 || rnd() > 0.85) return;
      const rot = Math.round(Math.atan2(-x, -z) / (PI / 2)) * (PI / 2);
      if (rnd() < 0.5) P('v_house', x, z, rot, 6.2 + rnd() * 1.2, 0, 0, 2);
      else P('village_house_b', x, z, rot, 6.0 + rnd() * 1.4, 0, 0, 2);
    });
  }
  scatter(1.25, 606, (x, z, d, rim) => {
    if (d > rim - 0.2 || rnd() > 0.5 * dens) return;
    (rnd() < (village ? 0.3 : 0.08) ? flowers : tufts).push({ x, y: 0, z, s: 0.5 + rnd() * 0.5, v: rnd() });
  });
  if (village) {
    // ── 광장 밀도(콘셉트 v-overview): 가장자리를 노점·화단·깃대·우물로 채우고 중앙 통로·시설 입구·분수 둘레는 비운다 ──
    const clear = villageClearZones(map);
    /** 발자국 사각형이 모두 광장 바닥이고 비울 구역과 겹치지 않으면 놓는다 */
    const put = (kind: KitKind, x: number, z: number, rot: number, h: number): boolean => {
      const [fw, fd] = footprintOf(kind, rot);
      const hx = fw / 2, hz = fd / 2;
      for (const [px, pz] of [[x - hx, z - hz], [x + hx, z - hz], [x - hx, z + hz], [x + hx, z + hz], [x, z]] as const) {
        if (cellW(px, pz) !== Cell.Floor) return false;
      }
      for (const c of clear) if (x + hx > c.x0 && x - hx < c.x1 && z + hz > c.z0 && z - hz < c.z1) return false;
      P(kind, x, z, rot, h, 0, 0, 1);
      return true;
    };
    const fl = map.floors[0];
    // 북쪽 줄(벽 앞, 정면이 카메라 쪽)과 남쪽 줄(정면이 광장 쪽): 통로에서 바깥으로 가로등 → 노점 → 깃대 → 노점 → 우물·화단
    const rows: { z: number; rot: number }[] = [
      { z: fl.z0 + 1.25, rot: 0 },
      { z: fl.z1 - 1.25, rot: PI },
    ];
    for (const { z, rot } of rows) {
      for (const side of [-1, 1]) {
        put('v_lamppost', side * 4.3, z + (rot === 0 ? -0.6 : 0.6), 0, 3.6);
        put('village_market_stall', side * 6.6, z, rot, 2.4);
        // 북쪽 줄은 깃대(카메라 쪽으로 깃발이 보인다), 남쪽 줄은 화단
        if (rot === 0) put('village_banner_pole', side * 9.3, z - 0.5, 0, 4.0);
        else put('village_flower_planter', side * 9.3, z, 0, 0.9);
        put('village_market_stall', side * 11.9, z, rot, 2.4);
        if (!put('village_well', side * 15.6, z, 0, 2.3)) put('village_flower_planter', side * 15.2, z, 0, 0.9);
      }
    }
    // 동서 가장자리(시설 사이 빈 벽): 우물·노점·화단·깃대
    put('village_well', fl.x0 + 1.6, -0.5, PI / 2, 2.3);
    put('village_flower_planter', fl.x0 + 1.2, 2.8, 0, 0.9);
    put('village_market_stall', fl.x1 - 1.25, 0.2, -PI / 2, 2.4);
    put('village_flower_planter', fl.x1 - 1.2, 3.4, 0, 0.9);
    put('village_banner_pole', fl.x1 - 0.8, -3.2, -PI / 2, 4.0);
    // 시설 입구 양옆 화단(문 앞은 비운다)
    map.landmarks.forEach((l) => {
      if (l.kind !== 'shop' && l.kind !== 'forge' && l.kind !== 'trainer' && l.kind !== 'board') return;
      const cx = (l.rect.x0 + l.rect.x1) / 2;
      if (l.kind === 'board') {
        put('village_flower_planter', l.rect.x0 - 0.9, l.rect.z1 + 0.2, 0, 0.9);
        return;
      }
      const fx = cx < 0 ? l.rect.x1 + 0.8 : l.rect.x0 - 0.8;
      for (const fz of [l.rect.z0 + 0.35, l.rect.z1 - 0.35]) {
        if (put('village_flower_planter', fx, fz, 0, 0.9)) {
          for (let k = 0; k < 3; k++) flowers.push({ x: fx + (rnd() - 0.5) * 1.8, y: 0, z: fz + (rnd() - 0.5) * 1.8, s: 0.45 + rnd() * 0.3, v: rnd() });
        }
      }
    });
    // 던전 문 앞 깃대 한 쌍(통로 양옆)
    for (const s of [-1, 1]) put('village_banner_pole', s * 4.6, fl.z0 + 2.9, 0, 4.4);
    // 광장 가장자리 꽃 화단(콘셉트 v-overview처럼 돌길 둘레가 꽃으로 덮인다): 벽 마디 하나 건너 하나
    segs.forEach((s, k) => {
      if (k % 2 === 1) return;
      const cx0 = s.x + s.nx * 1.0, cz0 = s.z + s.nz * 1.0;
      if (cellW(cx0, cz0) !== Cell.Floor || nearGate(cx0, cz0, 2.5)) return;
      for (let q = 0; q < Math.round(7 * opt.density) + 2; q++) {
        const u = (rnd() - 0.5) * s.len * 0.9, dd = rnd() * 1.1;
        const fx = cx0 + s.nx * dd + (s.nz !== 0 ? u : 0), fz = cz0 + s.nz * dd + (s.nx !== 0 ? u : 0);
        if (cellW(fx, fz) !== Cell.Floor) continue;
        (rnd() < 0.7 ? flowers : tufts).push({ x: fx, y: 0, z: fz, s: 0.55 + rnd() * 0.35, v: rnd() });
      }
    });
    // 동서 가장자리 가로등(시설 앞을 피해서)
    for (const [x, z] of [[fl.x0 + 1.3, -2.6], [fl.x1 - 1.3, -5.2], [fl.x0 + 1.3, 4.6], [fl.x1 - 1.3, 5.6]] as const) put('v_lamppost', x, z, 0, 3.6);
    // 분수 둘레 꽃: 대야 돌 테두리 밑동에 붙은 화단 무리 7개(콘셉트 v-overview·style-B처럼 잎 바닥 위에 꽃이 모여 핀다).
    // 한 송이씩 고르게 흩으면 맨 돌길 위에 떠 있는 점으로 읽혔다(2026-10-15 실화면). 꽃 34, 무리마다 풀 포기 3장.
    // 대야 뒤(북쪽, c = 3·4)는 카메라에서 가리므로 꽃을 줄이고 보이는 무리에 몰아준다.
    // 풀 포기는 꽃보다 낮게(카드 높이 약 0.35~0.45m < 꽃 0.4~0.6m): 높으면 무리가 연두 풀로 읽히고 꽃이 가린다(1차 캡처).
    // 무리는 밑동 호를 따라 두 줄로 엇갈린다: 접선 직선으로 펴면 끝이 밑동에서 떨어져 맨 돌 위 일렬로 읽혔다(후속 회차).
    // 분수 키트 밑동(아래 8%) 반경 = 배치 폭의 0.495~0.514(GLB 실측) → 폭 6.3m에서 약 3.12~3.24m.
    const fountain = map.landmarks.find((l) => l.kind === 'fountain');
    if (fountain) {
      const fr = fountain.rect;
      const fcx = (fr.x0 + fr.x1) / 2, fcz = (fr.z0 + fr.z1) / 2;
      // 키트는 충돌보다 5% 크게 놓인다(위 'fountain' 배치). base = 밑동 반경
      const base = Math.max(fr.x1 - fr.x0, fr.z1 - fr.z0) * 1.05 * 0.505;
      const N = 7;
      // 무리별 꽃 수(합 34): 앞 4(낮은 둔덕), 대야 뒤 3·4, 나머지 5~6으로 고르지 않게
      const COUNT = [4, 6, 6, 3, 4, 6, 5];
      for (let c = 0; c < N; c++) {
        // c = 0은 카메라 쪽(+z) 정면: 대야 정면이 맨 돌로 남지 않게 낮은 무리 하나만 둔다
        const a = PI / 2 + (c / N) * PI * 2 + (c === 0 ? 0 : (rnd() - 0.5) * 0.35);
        const front = c === 0;
        // 밑동 호 위의 점: along = 호 길이(m), r = 반경(m)
        const at = (along: number, r: number): [number, number] => {
          const t = a + along / r;
          return [fcx + Math.cos(t) * r, fcz + Math.sin(t) * r];
        };
        // 호 길이 1.2m 이하(앞 무리는 0.9m)
        const span = front ? 0.9 : 1.2;
        for (let q = 0; q < 3; q++) {
          // 잎 바닥은 밑동 돌에 살짝 묻힌다(반경 base~base+0.15)
          const [x, z] = at((q - 1) * span * 0.4 + (rnd() - 0.5) * 0.16, base + 0.02 + rnd() * 0.13);
          tufts.push({ x, y: 0, z, s: front ? 0.33 : 0.36 + rnd() * 0.1, v: rnd() });
        }
        for (let q = 0, n = COUNT[c]; q < n; q++) {
          // 두 줄 엇갈림: 안쪽 줄(돌에 붙음)은 조금 높게, 바깥 줄은 약 0.24m 밖에 낮게(바깥 끝 반경 약 3.55m 이하)
          const inner = q % 2 === 0;
          const along = (n > 1 ? q / (n - 1) - 0.5 : 0) * span + (rnd() - 0.5) * 0.16;
          const [x, z] = at(along, base + (inner ? 0.07 + rnd() * 0.05 : 0.31 + rnd() * 0.05));
          const s0 = front ? 0.5 + rnd() * 0.08 : 0.62 + rnd() * 0.2;
          flowers.push({ x, y: 0, z, s: inner ? s0 + 0.1 : s0, v: rnd() });
        }
      }
    }
  }

  // 바닥 그늘: 바닥 셀에서 가장 가까운 빈칸까지 거리(벽 밑 1.5m 안쪽이 어둡고 이끼가 진하다)
  const shadeData = new Uint8Array(W * H);
  for (let gz = 0; gz < H; gz++) for (let gx = 0; gx < W; gx++) {
    if (cell(gx, gz) === Cell.Void) { shadeData[gz * W + gx] = 255; continue; }
    let best = 9;
    for (let dz = -3; dz <= 3; dz++) for (let dx = -3; dx <= 3; dx++) {
      if (cell(gx + dx, gz + dz) !== Cell.Void || gateCell(gx, gz)) continue;
      const dd = Math.max(0, Math.hypot(dx, dz) - 0.5);
      if (dd < best) best = dd;
    }
    shadeData[gz * W + gx] = Math.round(Math.max(0, 1 - best / 2.2) * 255);
  }

  const lamps: Emitter[] = [];
  for (const p of placements) if (p.kind === 'v_lamppost') lamps.push({ x: p.x, y: p.h * 0.86, z: p.z, s: 1.5, v: (p.x * 7.3 + p.z * 3.1) % 1 });
  // 담쟁이(콘셉트 r1-dungeon·c-boss-fight의 덩굴 덮인 벽): 무너지지 않은 벽 안쪽 면 윗끝에서 늘어뜨린다. 문 앞은 비운다.
  // 마디(2m)마다 넓은 카드 2~3장이면 벽이 연속된 녹색 상자 덤불로 읽혔다(2026-10-15 보스방 실화면).
  // 마디당 0~2장(평균 1), 좁고 길이가 다른 가닥으로 돌벽이 드러나고 덩굴이 군데군데 늘어지게 한다.
  // 따로 난수를 써서 다른 배치의 시드 흐름을 바꾸지 않는다.
  const ivy: (Emitter & { rot: number; w: number })[] = [];
  if (!village) {
    const rndIvy = mulberry32(opt.seed + 4242);
    for (const s of segs) {
      if (s.broken || nearGate(s.x, s.z, 2)) continue;
      const roll = rndIvy();
      const n = roll < 0.3 ? 0 : roll < 0.7 ? 1 : 2;
      for (let k = 0; k < Math.round(n * (0.5 + 0.5 * opt.density)); k++) {
        const u = (rndIvy() - 0.5) * s.len * 0.8;
        ivy.push({ x: s.x + s.nx * (0.05 + k * 0.03) + (s.nz !== 0 ? u : 0), y: 3.0, z: s.z + s.nz * (0.05 + k * 0.03) + (s.nx !== 0 ? u : 0), s: 1.0 + rndIvy() * 1.6, v: rndIvy(), rot: Math.atan2(s.nx, s.nz), w: 0.6 + rndIvy() * 0.6 });
      }
    }
  }
  return { placements, flames, glows, tufts, flowers, shafts, pools, lamps, ivy, plateau, shade: { originX: ox, originZ: oz, width: W, height: H, data: shadeData } };
}
